import { Loader2 } from 'lucide-react';
import { useFormState } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { SheetFooter } from '@/components/ui/sheet';

import type { ContractForm } from './types';

interface ContractFormFooterProps {
  variant: 'success' | 'disabled' | 'submit';
  onClose: () => void;
  isSubmitting?: boolean;
  hookForm?: ContractForm;
}

export function ContractFormFooter(props: ContractFormFooterProps) {
  const { onClose } = props;

  if (props.variant === 'success') {
    return (
      <SheetFooter className="shrink-0 justify-end">
        <Button type="button" onClick={onClose}>
          <FormattedMessage
            defaultMessage="Done"
            description="Close add contract sheet after successful contract creation"
          />
        </Button>
      </SheetFooter>
    );
  }

  if (props.variant === 'disabled') {
    return (
      <SheetFooter className="shrink-0">
        <Button variant="secondary" type="button" onClick={onClose}>
          <FormattedMessage
            defaultMessage="Cancel"
            description="Close add contract sheet without saving"
          />
        </Button>
        <Button type="button" disabled>
          <FormattedMessage
            defaultMessage="Add Contract"
            description="Add contract sheet: submit button to save the new contract"
          />
        </Button>
      </SheetFooter>
    );
  }

  if (props.hookForm == null) return null;

  return (
    <ContractFormSubmitFooter
      hookForm={props.hookForm}
      onClose={onClose}
      isSubmitting={props.isSubmitting}
    />
  );
}

function ContractFormSubmitFooter({
  hookForm,
  onClose,
  isSubmitting,
}: {
  hookForm: ContractForm;
  onClose: () => void;
  isSubmitting?: boolean;
}) {
  const { isValid } = useFormState({ control: hookForm.control });

  return (
    <SheetFooter className="shrink-0">
      <Button
        variant="secondary"
        type="button"
        onClick={onClose}
        disabled={isSubmitting ?? false}
      >
        <FormattedMessage
          defaultMessage="Cancel"
          description="Close add contract sheet without saving"
        />
      </Button>
      <Button
        type="submit"
        form="add-contract-form"
        disabled={!isValid || (isSubmitting ?? false)}
      >
        {(isSubmitting ?? false) && <Loader2 className="animate-spin" />}
        <FormattedMessage
          defaultMessage="Add Contract"
          description="Add contract sheet: submit button to save the new contract"
        />
      </Button>
    </SheetFooter>
  );
}
